import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ResponseEnvelope } from '../models/response-envelope';

/** What the browser hands us, plus a human label so a device is recognisable on screen. */
export interface PushSubscriptionBody {
  /**
   * THE KEY the backend upserts on — not `endpoint`.
   *
   * An endpoint is re-minted by the browser on every VAPID rotation, on `pushsubscriptionchange`
   * and whenever permission is cycled, so a row keyed by it duplicated whenever two registrations
   * each minted one. This id is stable for the life of the browser profile.
   */
  deviceId: string;
  endpoint: string;
  keys: { p256dh: string; auth: string };
  label: string;
  /**
   * The secret a previous registration returned for this device, when this browser has one.
   *
   * What makes `deviceId` safe to key a row on: the id is echoed on every heartbeat and has no
   * secrecy anywhere, and registration reads a foreign row as a HANDOVER, so without this anyone
   * who learned an id could present it with an endpoint of their own and take the row over — the
   * victim's browser stops receiving until it next re-registers. OPTIONAL on the wire: a first
   * registration has not been given one, and a client that lost its `localStorage` is re-issued one
   * for a row it already owns. Send NOTHING rather than a blank — the backend reads both the same
   * way, and only one of them fits its length constraint.
   */
  deviceSecret?: string;
}



/**
 * One browser belonging to one user, from `GET /v2/push/users/{userId}/devices`.
 *
 * Covers BOTH halves: a device with a live subscription, and a device that only ANSWERED —
 * including one that refused, which has no subscription and is invisible everywhere else. A
 * subscription is a MACHINE, and a shared one belongs to whoever logged in last, so "what is this
 * row" is only answerable per device. Carries no endpoint and no
 * keys — those are push credentials and an admin screen has no use for them.
 */
export interface PushDeviceRow {
  label: string | null;
  /** `granted`, `denied` or `default` — the browser's own vocabulary, stored verbatim. */
  state: string;
  shared: boolean;
  /** Whether a live subscription exists. A `denied` device never has one — that is the point. */
  subscribed: boolean;
  lastSeenAt: string | null;
  lastPushAt: string | null;
  failureCount: number;
  updatedAt: string | null;
}

/**
 * `POST /v2/push/devices` now answers 200 with the row's stored `shared` value (was a bare
 * 204) — the heartbeat itself is how the frontend learns this device's mode, with no second read
 * and no second source of truth. See the phase-2 design doc.
 */
export interface PushRegisterResponse {
  shared: boolean;
  /**
   * A newly minted device secret to STORE, present only when one was minted — a first
   * registration, an adopted row, or a caller that could not produce the old one. The server keeps
   * only its SHA-256, so this response is the one and only place the value exists outside this
   * browser.
   *
   * <p>Absent means "keep the secret you already have", NOT "you have none": the field is omitted
   * (`NON_NULL`) precisely when the registration PROVED possession.
   */
  deviceSecret?: string;
}

/**
 * Carved out of the interceptor's 401 handling — see SharedHttpInterceptor. The carve-out covers
 * EVERY method on this URL, not just the teardown DELETE: since the heartbeat, the POST is fired
 * by a token rotation rather than by a user gesture (see PushNotificationService.heartbeat).
 *
 * The interceptor matches it with its OWN anchored regex rather than importing this string, so a
 * sibling path (`/v2/push/devices/permission`, `/v2/push/devices/{id}`) cannot inherit the
 * exemption by prefix — and one of those siblings is REAL: the permission report lives under this
 * path and must keep its 401 refresh-retry. `http.interceptors.spec.ts` drives the carve-out
 * through this exported constant, so the literal there and the URL called here cannot drift
 * without a test going red.
 */
export const PUSH_DEVICES_ENDPOINT = '/v2/push/devices';

@Injectable({ providedIn: 'root' })
export class PushApiService {
  private base = `${environment.apiUrl}/v2/push`;

  constructor(private http: HttpClient) {}

  /** Fetched rather than bundled: a key baked into environment.ts cannot be rotated
   *  without a redeploy, and rotation invalidates every existing subscription.
   *
   *  THE KEY ITSELF IS GUARDED, NOT ONLY THE ENVELOPE. `noteCapability` asks whether the answer is
   *  `''`, so an envelope that arrives with `data` present but `publicKey` absent or null would
   *  hand it `undefined` — truthy-adjacent enough to leave `capable` true, which is precisely the
   *  field-found bug the capability gate exists to close: `enable()` then reaches
   *  `Notification.requestPermission()` and can permanently block a device for a feature this
   *  server never had switched on. The wire really can omit it — the response envelopes here are
   *  `NON_NULL` (see `deviceSecret` in `subscribe` below), so an absent key is a shape this
   *  endpoint can legitimately produce when push is off. Downstream it is worse still:
   *  `urlBase64ToUint8Array(undefined)` throws inside `trySubscribeAndPost` and surfaces as a
   *  bogus `inactive` on a healthy device. */
  vapidKey(): Observable<string> {
    return this.http
      .get<
        ResponseEnvelope<{ publicKey?: string | null }>
      >(`${this.base}/vapid-key`, { withCredentials: true })
      // `res?.` and not only `res.data?.`: an empty body (a backend that still answers the bare 204
      // this endpoint used to, or any 200 with nothing in it) reaches the mapper as `null`, and
      // `null.data` throws a TypeError INSIDE the map -- which surfaces as a failed key read rather
      // than as "this server has no key". Reading it as `''` keeps the answer the envelope's own
      // absent/null cases already give: the capability gate hides the icon and, crucially, refuses
      // to prompt.
      .pipe(map((res) => res?.data?.publicKey ?? ''));
  }

  subscribe(body: PushSubscriptionBody): Observable<PushRegisterResponse> {
    return this.http
      .post<
        ResponseEnvelope<PushRegisterResponse>
      >(`${this.base}/devices`, body, { withCredentials: true })
      // `?? true` — fail CLOSED, like every other default in this feature (sharedSubject, the icon
      // field, the dialog field and the DB column all default `true`). A response the frontend
      // cannot read must not silently downgrade a warehouse terminal to `personal`, which is the
      // mode exempt from the 60-minute TTL this phase exists to enforce.
      //
      // `deviceSecret` is carried through rather than flattened away: it is returned once and never
      // again, so a mapper that drops it loses it for good and this browser is a stranger to its own
      // row from the next registration on. The key is OMITTED rather than set to `undefined`, the
      // way the wire omits it (`NON_NULL`) — the two readings differ ("keep the one you have" is not
      // "you were handed nothing"), and only one of them survives a `toEqual`.
      .pipe(
        // `res?.` for the same reason as `vapidKey` above -- and it matters more here. Against a
        // backend still answering the bare 204 this endpoint used to, `res.data` throws inside the
        // map, so a registration that SUCCEEDED server-side is reported as a failure:
        // `setPostedEndpoint` is never written, the icon sticks at `inactive`, and every Reactivate
        // press repeats the same round trip forever. Unreadable still means `shared: true` (fail
        // closed) and no minted secret.
        map((res) => {
          const minted = res?.data?.deviceSecret;
          return { shared: res?.data?.shared ?? true, ...(minted ? { deviceSecret: minted } : {}) };
        }),
      );
  }

  /** The toggle's only caller. Idempotent and silent on an unknown/foreign device (204, no body) — matches `unsubscribe` below. */
  setShared(deviceId: string, shared: boolean): Observable<void> {
    return this.http
      .patch<ResponseEnvelope<null>>(
        `${this.base}/devices`,
        { deviceId, shared },
        { withCredentials: true },
      )
      .pipe(map(() => undefined));
  }

  /**
   * Stop delivering to this browser.
   *
   * By device id, not endpoint: this clears the delivery half of a row we own, and identifying it
   * by a value the browser may already have replaced would simply miss. The row survives, carrying
   * what this browser answered when asked for permission.
   */
  unsubscribe(deviceId: string): Observable<void> {
    return this.http
      .request<ResponseEnvelope<null>>('DELETE', `${this.base}/devices`, {
        body: { deviceId },
        withCredentials: true,
      })
      .pipe(map(() => undefined));
  }

  /**
   * Tell the backend what THIS browser answered when asked for notification permission.
   *
   * The only way a REFUSAL becomes visible: a denied browser has no subscription, so without this
   * it is indistinguishable from one that was never asked. Reported for every state, not only
   * denial — granting after a refusal must clear the record, or an admin keeps chasing someone who
   * fixed it days ago.
   */
  reportPermission(deviceId: string, state: NotificationPermission, label: string): Observable<void> {
    return this.http
      .post<void>(
        `${this.base}/devices/permission`,
        { deviceId, state, label },
        { withCredentials: true },
      )
      .pipe(map(() => undefined));
  }

  /** Every browser known for one user, refusals included. Admin-scoped, guarded server-side. */
  devices(userId: number): Observable<PushDeviceRow[]> {
    return this.http
      .get<
        ResponseEnvelope<PushDeviceRow[]>
      >(`${this.base}/users/${userId}/devices`, { withCredentials: true })
      // `res?.` -- see `vapidKey`. An empty body is an empty list, not a thrown TypeError.
      .pipe(map((res) => res?.data ?? []));
  }
}
